import {CoreGameConfig, IEventManagerWithAsync, IService, SystemComponentType, ScreenOrientation, Size2D} from '@gongxigames/framework/core/types';
import {CoreDI, CoreSettings} from '@gongxigames/framework/core/configs';
import {CoreEventsMap} from '@gongxigames/framework/core/events';
import {ViewportCalculator} from '@gongxigames/framework/core/services/screenManager/ViewportCalculator';
import {AppViewportConfig} from '@gongxigames/framework/core/services/screenManager/types';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {ScreenParameters} from '@gongxigames/framework/core/fragments/ScreenParameters';
import {System} from '@gongxigames/framework/core/services/system/System';
import {ResolutionManager} from '@gongxigames/framework/core/services/ResolutionManager';
import {ScreenManagerV2} from '@gongxigames/framework/core/services/screenManagerV2/ScreenManagerV2';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';
import type {ResizeEventData} from '@gongxigames/framework/core/types';

/**
 * @deprecated use only for slotV1 games
 */
@injectable()
export class ScreenManager implements IService {
    public static NAME: symbol = CoreDI.ScreenManager;
    public type: SystemComponentType = 'service';

    private _viewportCalculator!: ViewportCalculator;
    private _orientation?: ScreenOrientation;

    @inject(CoreDI.System)
    private readonly _system!: System;

    @inject(CoreDI.GameConfig)
    private readonly _gameConfig!: CoreGameConfig;

    @inject(CoreDI.Settings)
    private readonly _settings!: CoreSettings;

    @inject(CoreDI.IEventManagerWithAsync)
    private readonly _eventManager!: IEventManagerWithAsync<CoreEventsMap>;

    @inject(CoreDI.ResolutionManager)
    private readonly _resolutionManager!: ResolutionManager;

    @inject(CoreDI.ScreenManagerV2)
    private readonly _screenManagerV2!: ScreenManagerV2;

    private _viewport!: Size2D;
    private _defaultViewportResolution!: number;

    public mobilePortraitModeScale = 1;

    public init(): void {
        const platform = this._settings.get('platform');
        const isMobile = platform === 'mobile';
        const resolution = this._resolutionManager.resolution;

        this.mobilePortraitModeScale = this._gameConfig.viewport.mobile.height / this._gameConfig.viewport.mobile.width;
        this._viewport = isMobile ? this._gameConfig.viewport.mobile : this._gameConfig.viewport.desktop;
        this._viewportCalculator = new ViewportCalculator(this.getDeviceViewConfig(isMobile));

        this._defaultViewportResolution = resolution.width / this._viewport.width;

        strictCoreEmitter.on('CORE.screen.resize', this.onResize.bind(this));
    }

    public getScreenState(): ScreenParameters {
        return this._viewportCalculator.getScreenState();
    }

    public onResize(data: ResizeEventData): void {
        this._viewportCalculator.update(data.width, data.height);

        this.resize();
        this.sendUpdateEvents(data.forceUpdate);
    }

    public get orientation(): ScreenOrientation {
        return this._orientation!;
    }

    public isLandscapeOrientation(): boolean {
        return this._orientation === ScreenOrientation.LANDSCAPE;
    }

    public isPortraitOrientation(): boolean {
        return this._orientation === ScreenOrientation.PORTRAIT;
    }

    public get width(): number {
        return this._viewport.width;
    }

    public get height(): number {
        return this._viewport.height;
    }

    /**
     * "PixelRatio" for old games generated text and sprites quality.
     */
    public get defaultViewportResolution(): number {
        return this._defaultViewportResolution;
    }

    private resize(): void {
        const {scale, x, y, canvasWidth, canvasHeight} = this._viewportCalculator.getScreenState();
        const app = this._system.app;

        app.renderer.resize(canvasWidth, canvasHeight);
        app.stageV1.scale.set(scale);
        app.stageV1.position.set(x, y);
    }

    private sendUpdateEvents(forceUpdate: boolean): void {
        const state = this._viewportCalculator.getScreenState();
        const {orientation} = state;

        this._eventManager.emit('General.Screen.RESIZED', state);

        if (this._orientation !== orientation || forceUpdate) {
            const mode = this.getScreenMode(orientation);

            this._orientation = orientation;
            this._eventManager.emit('General.Screen.ORIENTATION_CHANGED', this._orientation);
            this._eventManager.emit(mode);
        }
    }

    private getScreenMode(orientation: ScreenOrientation): 'General.Screen.LANDSCAPE' | 'General.Screen.PORTRAIT' {
        return orientation === ScreenOrientation.LANDSCAPE ? 'General.Screen.LANDSCAPE' : 'General.Screen.PORTRAIT';
    }

    private getDeviceViewConfig(isMobilePlatform: boolean): AppViewportConfig {
        const {width, height} = this._viewport;
        const forceOrientation = this._screenManagerV2.getForceOrientation();

        const result: AppViewportConfig = {
            forceOrientation,
            safeZone: {
                [ScreenOrientation.LANDSCAPE]: {width, height}
            }
        };

        if (isMobilePlatform) {
            result.safeZone[ScreenOrientation.PORTRAIT] = {height: width, width: height};
        }

        return result;
    }
}
